import axios from 'axios';

const api = axios.create({
  baseURL: window.location.host.includes('localhost') ? 'http://localhost:8080' : window.location.origin + '/rys-ws',
});

const MAX_TRANSIENT_RETRIES = 6;
const RETRY_BASE_DELAY_MS = 1000;

function shouldRetryRequest(error) {
  const status = error?.response?.status;
  const method = (error?.config?.method || 'get').toLowerCase();
  const idempotentMethod = ['get', 'head', 'options'].includes(method);
  const transientStatus = status === 502 || status === 503 || status === 504;
  const networkError = !error?.response;
  return idempotentMethod && (networkError || transientStatus);
}

function getRetryDelay(attempt) {
  return Math.min(RETRY_BASE_DELAY_MS * (2 ** (attempt - 1)), 8000);
}

// Add a request interceptor to include JWT if present
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Global response handler: if token is invalid/expired, clear and redirect to admin login
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config || {};
    config.__retryCount = config.__retryCount || 0;

    if (shouldRetryRequest(error) && config.__retryCount < MAX_TRANSIENT_RETRIES) {
      config.__retryCount += 1;
      const delay = getRetryDelay(config.__retryCount);
      await new Promise((resolve) => setTimeout(resolve, delay));
      return api(config);
    }

    const status = error?.response?.status;
    if (status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('adminRole');
      try {
        window.location.href = '/admin';
      } catch (e) {
        /* ignore */
      }
    }
    return Promise.reject(error);
  }
);

// Winners API
export const getWinnersData = async (year, type) => {
  return api.get(`/admin/api/winners/${year}/${type.toLowerCase()}`);
};

export const markWinner = async (year, type, schoolId) => {
  return api.post(`/admin/api/winners/${year}/${type.toLowerCase()}/${schoolId}`);
};

export const unmarkWinner = async (year, type, schoolId) => {
  return api.delete(`/admin/api/winners/${year}/${type.toLowerCase()}/${schoolId}`);
};

export const getWinnerAssetConfig = () => api.get('/admin/api/winner-assets/config');
export const getWinnerAssetOverview = (year) => api.get(`/admin/api/winner-assets/${year}/overview`);
export const getWinnerAssetSchool = (year, schoolId) => api.get(`/admin/api/winner-assets/${year}/schools/${schoolId}`);
export const getProjectKickoffRegistrations = () => api.get('/admin/api/project-kickoff-registrations');

export default api;
